import React, { useEffect, useRef, useState } from 'react';
import { useCheckout } from '../CheckoutContext';
import { useLang } from '../i18n/LanguageContext';

const MESSAGES = {
  fr: [
    'Préparation de ton espace',
    'On personnalise ton accompagnement',
    'Connexion au portail OpenBro',
    'Ton accès est en cours d\'activation',
  ],
  en: [
    'Preparing your space',
    'Personalizing your coaching',
    'Connecting to the OpenBro portal',
    'Activating your access',
  ],
};

const CheckoutPortal = () => {
  const { isActive, checkoutUrl } = useCheckout();
  const { lang } = useLang();
  const messages = MESSAGES[lang] || MESSAGES.fr;
  const canvasRef = useRef(null);
  const [msgIndex, setMsgIndex] = useState(0);
  const [dots, setDots] = useState('');
  const redirected = useRef(false);

  // Redirect as soon as URL is ready
  useEffect(() => {
    if (checkoutUrl && !redirected.current) {
      redirected.current = true;
      window.location.href = checkoutUrl;
    }
  }, [checkoutUrl]);

  // Cycle messages
  useEffect(() => {
    if (!isActive) return;
    const t = setInterval(() => setMsgIndex((i) => (i + 1) % messages.length), 2200);
    return () => clearInterval(t);
  }, [isActive, messages.length]);

  // Dots animation
  useEffect(() => {
    if (!isActive) return;
    const t = setInterval(() => setDots((d) => d.length >= 3 ? '' : d + '.'), 400);
    return () => clearInterval(t);
  }, [isActive]);

  // Reset on unmount
  useEffect(() => {
    if (!isActive) { redirected.current = false; }
  }, [isActive]);

  // Canvas: silver star warp
  useEffect(() => {
    if (!isActive) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const DEPTH = 1200;
    const makeStar = (z) => ({
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: z ?? Math.random() * DEPTH,
      pz: null,
      tint: Math.random() < 0.25 ? '200, 215, 255' : '235, 240, 250',
    });
    const stars = Array.from({ length: 260 }, () => makeStar());

    const draw = () => {
      time += 0.016;
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      const focal = Math.min(w, h) * 0.9;
      const speed = 2.2 + Math.min(time, 3) * 1.4;

      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        s.z -= speed;
        if (s.z <= 1) { Object.assign(s, makeStar(DEPTH)); s.pz = null; continue; }
        const k = focal / s.z;
        const sx = cx + s.x * k * cx;
        const sy = cy + s.y * k * cy;
        const pk = focal / (s.z + speed * 2.5);
        const px = cx + s.x * pk * cx;
        const py = cy + s.y * pk * cy;
        if (sx < 0 || sx > w || sy < 0 || sy > h) { Object.assign(s, makeStar(DEPTH)); continue; }
        const depth = 1 - s.z / DEPTH;
        const alpha = Math.min(1, depth * 1.3) * 0.9;
        const size = Math.max(0.3, depth * 2.2);

        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(sx, sy);
        ctx.strokeStyle = `rgba(${s.tint}, ${alpha * 0.55})`;
        ctx.lineWidth = size;
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(sx, sy, size * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.tint}, ${alpha})`;
        ctx.fill();
      }

      // Soft horizon glow (Earth atmosphere) at the bottom
      const grd = ctx.createRadialGradient(cx, h * 1.35, h * 0.2, cx, h * 1.35, h * 1.1);
      grd.addColorStop(0, 'rgba(120, 170, 255, 0.22)');
      grd.addColorStop(0.55, 'rgba(60, 100, 200, 0.06)');
      grd.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, w, h);

      animationId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animationId); window.removeEventListener('resize', resize); };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#020409' }}>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/hero-earth.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          animation: 'obPortalZoom 14s ease-out forwards',
          opacity: 0.55,
        }}
      />
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at center, rgba(2,4,9,0.45) 0%, rgba(2,4,9,0.8) 60%, rgba(2,4,9,0.97) 100%)' }}
      />
      <canvas ref={canvasRef} className="absolute inset-0" />

      <div className="relative z-10 text-center px-6" style={{ fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif" }}>
        <p style={{ fontSize: 10, letterSpacing: '0.32em', color: 'rgba(255,255,255,0.7)', marginBottom: 14 }}>
          OPENBRO
        </p>
        <h1
          className="text-[56px] sm:text-[76px] lg:text-[96px] leading-[1]"
          style={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#fff', margin: 0 }}
        >
          Open
          <span style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #b9c3d6 55%, #6f7a90 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}>Bro</span>
        </h1>
        <p style={{ marginTop: 14, fontSize: 16, fontWeight: 300, letterSpacing: '0.32em', color: 'rgba(255,255,255,0.9)' }}>
          A BRIGHTER TOMORROW.
        </p>

        <p className="mt-10 text-[14px]" style={{ letterSpacing: '0.04em', color: 'rgba(255,255,255,0.65)' }}>
          {messages[msgIndex]}{dots}
        </p>

        {/* Loading bar */}
        <div className="mt-4 mx-auto w-[220px] h-[1px] overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div
            className="h-full"
            style={{
              background: 'linear-gradient(90deg, #8b96ad, #ffffff)',
              width: checkoutUrl ? '100%' : '60%',
              transition: checkoutUrl ? 'width 0.3s ease-out' : 'width 8s ease-out',
              boxShadow: '0 0 12px rgba(255,255,255,0.6)',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes obPortalZoom {
          from { transform: scale(1); }
          to { transform: scale(1.18); }
        }
      `}</style>
    </div>
  );
};

export default CheckoutPortal;
